import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  FileText, Plus, Trash2, Edit2, Check, X, AlertTriangle, GripVertical,
  Layers, Target, ChevronRight, ChevronDown, Flag, Clock, User,
  CheckCircle, Info, Loader2, BookTemplate, Shield, Link, Filter,
  Search, RefreshCw, Sparkles
} from 'lucide-react';
import { api } from '../api/client';
import { toast } from 'sonner';
import { normalizeRequirement, adaptRequirementsResponse } from '../utils/dataAdapters';
import RequirementEditorModal, { QualityBadge } from './RequirementEditor';
import { WidgetErrorBoundary } from './ErrorBoundary';
import { SkeletonTreeList } from './Skeleton';
import EmptyState from './EmptyState';

// ==========================================
// CONSTANTS
// ==========================================

const HIERARCHY_ICONS = {
  theme: Layers,
  epic: Target,
  requirement: FileText,
};

const HIERARCHY_COLORS = {
  theme: 'bg-purple-100 text-purple-700 border-purple-200',
  epic: 'bg-blue-100 text-blue-700 border-blue-200',
  requirement: 'bg-green-100 text-green-700 border-green-200',
};

const CATEGORY_COLORS = {
  'Functional': 'bg-blue-100 text-blue-700',
  'Non-Functional': 'bg-purple-100 text-purple-700',
  'Integration': 'bg-cyan-100 text-cyan-700',
  'Reporting': 'bg-amber-100 text-amber-700',
  'Compliance': 'bg-red-100 text-red-700',
  'UX': 'bg-pink-100 text-pink-700',
  'Technical': 'bg-gray-100 text-gray-700',
};

const PRIORITY_COLORS = {
  'Must Have': 'bg-red-100 text-red-700',
  'Should Have': 'bg-amber-100 text-amber-700',
  'Could Have': 'bg-blue-100 text-blue-700',
  "Won't Have": 'bg-gray-100 text-gray-600',
};

// ==========================================
// SORTABLE REQUIREMENT ITEM
// ==========================================

const SortableRequirementItem = ({
  requirement,
  depth,
  isExpanded,
  onToggle,
  onEdit,
  onDelete,
  onAddChild,
  onAIAssist,
  onSelect,
  isReadOnly,
  expandedIds,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: requirement.id, disabled: isReadOnly });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    paddingLeft: `${depth * 24 + 12}px`,
    opacity: isDragging ? 0.5 : 1,
  };

  const hasChildren = requirement.children && requirement.children.length > 0;
  const Icon = HIERARCHY_ICONS[requirement.hierarchy_type] || FileText;
  const typeColor = HIERARCHY_COLORS[requirement.hierarchy_type] || 'bg-gray-100 text-gray-700 border-gray-200';
  const canHaveChildren = requirement.hierarchy_type !== 'requirement';

  // Quality indicators
  const quality = requirement.quality_flags || {};
  const hasIssues = quality.missing_ac || quality.missing_category || quality.ambiguous_language;

  return (
    <div ref={setNodeRef} style={style} className="select-none" data-testid={`req-item-${requirement.id}`}>
      <div 
        onClick={() => onSelect?.(requirement)}
        className={`flex items-center gap-2 py-2 px-3 rounded-lg hover:bg-gray-50 transition-colors group cursor-pointer ${
          isDragging ? 'bg-blue-50 shadow-lg' : ''
        }`}
      >
        {/* Drag handle */}
        {!isReadOnly && (
          <button 
            {...attributes} 
            {...listeners}
            className="p-1 cursor-grab hover:bg-gray-200 rounded opacity-0 group-hover:opacity-100 transition-opacity"
            data-testid={`drag-handle-${requirement.id}`}
          >
            <GripVertical className="w-4 h-4 text-gray-400" />
          </button>
        )}

        {/* Expand/collapse */}
        {hasChildren ? (
          <button 
            onClick={() => onToggle(requirement.id)}
            className="p-0.5 hover:bg-gray-200 rounded"
            data-testid={`toggle-${requirement.id}`}
          >
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 text-gray-500" />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-500" />
            )}
          </button>
        ) : (
          <span className="w-5" />
        )}

        {/* Type badge */}
        <span className={`flex items-center gap-1 px-2 py-0.5 rounded border text-xs font-medium ${typeColor}`}>
          <Icon className="w-3 h-3" />
          {requirement.hierarchy_type}
        </span>

        {/* Name */}
        <span className="flex-1 text-sm text-gray-800 truncate font-medium">
          {requirement.name}
        </span>

        {/* Category badge (for requirements) */}
        {requirement.category && (
          <span className={`text-xs px-1.5 py-0.5 rounded ${CATEGORY_COLORS[requirement.category] || 'bg-gray-100'}`}>
            {requirement.category}
          </span>
        )}

        {/* Priority badge */}
        {requirement.priority && (
          <span className={`text-xs px-1.5 py-0.5 rounded ${PRIORITY_COLORS[requirement.priority] || 'bg-gray-100'}`}>
            {requirement.priority}
          </span>
        )}

        {/* AC count */}
        {requirement.acceptance_criteria?.length > 0 && (
          <span className="text-xs text-gray-500 flex items-center gap-0.5">
            <CheckCircle className="w-3 h-3" />
            {requirement.acceptance_criteria.length} AC
          </span>
        )}

        {/* Quality indicator */}
        {hasIssues && (
          <span className="flex items-center gap-1 text-amber-500" title="Quality issues detected">
            <AlertTriangle className="w-3.5 h-3.5" />
            <span className="text-xs">{quality.score}%</span>
          </span>
        )}

        {/* Actions */}
        {!isReadOnly && (
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {/* AI Assist button - only for leaf requirements */}
            {requirement.hierarchy_type === 'requirement' && onAIAssist && (
              <button 
                onClick={() => onAIAssist(requirement)}
                className="p-1 hover:bg-purple-100 rounded"
                title="AI Assist"
                data-testid={`ai-assist-btn-${requirement.id}`}
              >
                <Sparkles className="w-3.5 h-3.5 text-purple-500" />
              </button>
            )}
            <button 
              onClick={() => onEdit(requirement)}
              className="p-1 hover:bg-gray-200 rounded"
              title="Edit"
              data-testid={`edit-btn-${requirement.id}`}
            >
              <Edit2 className="w-3.5 h-3.5 text-gray-500" />
            </button>
            {canHaveChildren && (
              <button 
                onClick={() => onAddChild(requirement)}
                className="p-1 hover:bg-blue-100 rounded"
                title="Add child"
                data-testid={`add-child-${requirement.id}`}
              >
                <Plus className="w-3.5 h-3.5 text-blue-500" />
              </button>
            )}
            <button 
              onClick={() => onDelete(requirement)}
              className="p-1 hover:bg-red-100 rounded"
              title="Delete"
              data-testid={`delete-btn-${requirement.id}`}
            >
              <Trash2 className="w-3.5 h-3.5 text-red-500" />
            </button>
          </div>
        )}
      </div>

      {/* Render children recursively */}
      {isExpanded && hasChildren && (
        <div>
          {requirement.children.map(child => (
            <SortableRequirementItem
              key={child.id}
              requirement={child}
              depth={depth + 1}
              isExpanded={expandedIds.has(child.id)}
              onToggle={onToggle}
              onEdit={onEdit}
              onDelete={onDelete}
              onAddChild={onAddChild}
              onAIAssist={onAIAssist}
              onSelect={onSelect}
              isReadOnly={isReadOnly}
              expandedIds={expandedIds}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ==========================================
// MAIN REQUIREMENTS HIERARCHY COMPONENT
// ==========================================

const RequirementsHierarchy = ({
  projectId,
  tenantId,
  userId,
  baselineId = null,
  isReadOnly = false,
  onSelectionChange,
  showFilters = true,
  showHeader = true,
  onOpenAIAssist,
  onRequirementSelect,
  onDataLoaded,
}) => {
  // State
  const [loading, setLoading] = useState(true);
  const [requirements, setRequirements] = useState([]);
  const [categories, setCategories] = useState([]);
  const [hierarchyTypes, setHierarchyTypes] = useState({});
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [selectedId, setSelectedId] = useState(null);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterType, setFilterType] = useState('');
  
  // Editor modal
  const [showEditor, setShowEditor] = useState(false);
  const [editingRequirement, setEditingRequirement] = useState(null);
  const [parentForNew, setParentForNew] = useState(null);
  const [defaultHierarchyType, setDefaultHierarchyType] = useState('requirement');
  const [isSaving, setIsSaving] = useState(false);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Load data
  useEffect(() => {
    if (tenantId && projectId) {
      loadCategories();
      loadRequirements();
    }
  }, [tenantId, projectId, baselineId]);

  const loadCategories = async () => {
    try {
      const res = await api.getRequirementCategories();
      setCategories(res.data.categories || []);
      setHierarchyTypes(res.data.hierarchy_types || {});
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  };

  const loadRequirements = async () => {
    setLoading(true);
    try {
      // Load ALL requirements for the project (don't filter by baseline for hierarchy view)
      const res = await api.listRequirements(tenantId, projectId, {
        include_tree: true,
        // Don't filter by baseline_id - show all project requirements
      });
      const adapted = adaptRequirementsResponse(res.data);
      const reqs = adapted.requirements || [];
      setRequirements(reqs);
      
      // Notify parent of data loaded
      if (onDataLoaded) {
        onDataLoaded(reqs);
      }
      
      // Auto-expand root items
      const rootIds = new Set(reqs.map(r => r.id));
      setExpandedIds(rootIds);
    } catch (err) {
      console.error('Failed to load requirements:', err);
      toast.error('Failed to load requirements');
    } finally {
      setLoading(false);
    }
  };

  // Toggle expand/collapse
  const handleToggle = useCallback((id) => {
    setExpandedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  // Edit requirement
  const handleEdit = useCallback((requirement) => {
    setEditingRequirement(requirement);
    setParentForNew(null);
    setDefaultHierarchyType(requirement.hierarchy_type || 'requirement');
    setShowEditor(true);
  }, []);

  // Add child requirement
  const handleAddChild = useCallback((parent) => {
    setEditingRequirement(null);
    setParentForNew(parent);
    // Determine child type based on parent type
    const parentType = parent?.hierarchy_type;
    const childType = parentType === 'theme' ? 'epic' : parentType === 'epic' ? 'requirement' : 'requirement';
    setDefaultHierarchyType(childType);
    setShowEditor(true);
  }, []);

  // Add root requirement
  const handleAddRoot = useCallback((type = 'theme') => {
    setEditingRequirement(null);
    setParentForNew({ hierarchy_type: type === 'theme' ? null : type });
    setDefaultHierarchyType(type);
    setShowEditor(true);
  }, []);

  // Delete requirement
  const handleDelete = useCallback(async (requirement) => {
    const hasChildren = requirement.children && requirement.children.length > 0;
    const confirmMsg = hasChildren 
      ? `Delete "${requirement.name}" and all its children?`
      : `Delete "${requirement.name}"?`;
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      await api.deleteRequirement(requirement.id, tenantId, userId, hasChildren);
      toast.success('Requirement deleted');
      loadRequirements();
    } catch (err) {
      console.error('Failed to delete:', err);
      toast.error(err.response?.data?.detail || 'Failed to delete requirement');
    }
  }, [tenantId, userId]);

  // Save requirement (create or update)
  const handleSaveRequirement = async (formData) => {
    setIsSaving(true);
    try {
      // Clean up form data - convert empty strings to null for numeric fields
      const cleanedFormData = {
        ...formData,
        estimated_effort: formData.estimated_effort === '' || formData.estimated_effort === undefined 
          ? null 
          : parseFloat(formData.estimated_effort) || null,
        priority: formData.priority === '' ? null : formData.priority,
        owner: formData.owner === '' ? null : formData.owner,
      };

      if (editingRequirement) {
        // Update existing
        await api.updateRequirement(editingRequirement.id, tenantId, userId, cleanedFormData);
        toast.success('Requirement updated');
      } else {
        // Create new
        const parentId = parentForNew?.id || null;
        const parentType = parentForNew?.hierarchy_type;
        
        // Determine hierarchy type for new item
        let newType = cleanedFormData.hierarchy_type;
        if (parentType === 'theme') newType = 'epic';
        else if (parentType === 'epic') newType = 'requirement';
        
        await api.createRequirement(tenantId, userId, {
          ...cleanedFormData,
          project_id: projectId,
          parent_id: parentId,
          hierarchy_type: newType || cleanedFormData.hierarchy_type,
        });
        toast.success('Requirement created');
      }
      setShowEditor(false);
      loadRequirements();
    } catch (err) {
      console.error('Failed to save:', err);
      toast.error(err.response?.data?.detail || 'Failed to save requirement');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle DnD
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    // Find items in flat list
    const flatList = flattenRequirements(requirements);
    const activeIndex = flatList.findIndex(r => r.id === active.id);
    const overIndex = flatList.findIndex(r => r.id === over.id);

    if (activeIndex === -1 || overIndex === -1) return;

    // Calculate new order
    const newOrder = overIndex;
    
    try {
      await api.reorderRequirements(tenantId, userId, [
        { id: active.id, order: newOrder }
      ]);
      loadRequirements();
    } catch (err) {
      console.error('Failed to reorder:', err);
      toast.error('Failed to reorder requirements');
    }
  };

  // Flatten requirements for DnD
  const flattenRequirements = (reqs, result = []) => {
    for (const req of reqs) {
      result.push(req);
      if (req.children && expandedIds.has(req.id)) {
        flattenRequirements(req.children, result);
      }
    }
    return result;
  };

  // Get parent options for editor
  const getParentOptions = useMemo(() => {
    const options = [];
    const collectParents = (reqs) => {
      for (const req of reqs) {
        if (req.hierarchy_type !== 'requirement') {
          options.push(req);
        }
        if (req.children) collectParents(req.children);
      }
    };
    collectParents(requirements);
    return options;
  }, [requirements]);

  // Filter requirements
  const filteredRequirements = useMemo(() => {
    if (!searchQuery && !filterCategory && !filterType) return requirements;
    
    const filterTree = (reqs) => {
      return reqs.map(req => {
        const matchesSearch = !searchQuery || 
          req.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          req.description?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCategory = !filterCategory || req.category === filterCategory;
        const matchesType = !filterType || req.hierarchy_type === filterType;
        
        const filteredChildren = req.children ? filterTree(req.children) : [];
        const hasMatchingChildren = filteredChildren.length > 0;
        
        if ((matchesSearch && matchesCategory && matchesType) || hasMatchingChildren) {
          return { ...req, children: filteredChildren };
        }
        return null;
      }).filter(Boolean);
    };
    
    return filterTree(requirements);
  }, [requirements, searchQuery, filterCategory, filterType]);

  // Stats
  const stats = useMemo(() => {
    let themes = 0, epics = 0, reqs = 0, totalScore = 0, count = 0;
    const countItems = (items) => {
      for (const item of items) {
        if (item.hierarchy_type === 'theme') themes++;
        else if (item.hierarchy_type === 'epic') epics++;
        else reqs++;
        
        if (item.quality_flags?.score !== undefined) {
          totalScore += item.quality_flags.score;
          count++;
        }
        if (item.children) countItems(item.children);
      }
    };
    countItems(requirements);
    return {
      themes,
      epics,
      requirements: reqs,
      total: themes + epics + reqs,
      avgQuality: count > 0 ? Math.round(totalScore / count) : 0,
    };
  }, [requirements]);

  const flatItems = flattenRequirements(filteredRequirements);
  const itemIds = flatItems.map(r => r.id);

  if (loading) {
    return <SkeletonTreeList rows={8} />;
  }

  return (
    <WidgetErrorBoundary widgetName="Requirements Hierarchy">
      <div className="flex flex-col h-full" data-testid="requirements-hierarchy">
        {/* Header */}
        {showHeader && (
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Requirements Hierarchy</h3>
              <p className="text-sm text-gray-500">
                {stats.total} items: {stats.themes} themes, {stats.epics} epics, {stats.requirements} requirements
                {stats.avgQuality > 0 && (
                  <span className="ml-2">• Avg Quality: {stats.avgQuality}%</span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={loadRequirements}
                className="p-2 hover:bg-gray-100 rounded-lg"
                title="Refresh"
              >
                <RefreshCw className="w-4 h-4 text-gray-500" />
              </button>
              {!isReadOnly && (
                <button
                  onClick={() => handleAddRoot('theme')}
                  className="flex items-center gap-2 px-3 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:opacity-90"
                  data-testid="add-theme-btn"
                >
                  <Plus className="w-4 h-4" />
                  Add Theme
                </button>
              )}
            </div>
          </div>
        )}

        {/* Filters */}
        {showFilters && (
          <div className="flex items-center gap-3 mb-4 p-3 bg-gray-50 rounded-lg">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search requirements..."
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:outline-none"
                data-testid="search-requirements"
              />
            </div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:outline-none"
              data-testid="filter-type"
            >
              <option value="">All Types</option>
              <option value="theme">Theme</option>
              <option value="epic">Epic</option>
              <option value="requirement">Requirement</option>
            </select>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:outline-none"
              data-testid="filter-category"
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
            {(searchQuery || filterType || filterCategory) && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setFilterType('');
                  setFilterCategory('');
                }}
                className="p-2 hover:bg-gray-200 rounded-lg"
                title="Clear filters"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            )}
          </div>
        )}

        {/* Requirements Tree */}
        {filteredRequirements.length === 0 ? (
          <EmptyState
            icon={FileText}
            title="No requirements yet"
            description={searchQuery ? "No requirements match your search." : "Get started by adding your first theme."}
            action={!isReadOnly && !searchQuery && {
              label: "Add Theme",
              onClick: () => handleAddRoot('theme'),
            }}
          />
        ) : (
          <div className="flex-1 overflow-y-auto border border-gray-200 rounded-lg bg-white">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
                <div className="p-2">
                  {filteredRequirements.map(req => (
                    <SortableRequirementItem
                      key={req.id}
                      requirement={req}
                      depth={0}
                      isExpanded={expandedIds.has(req.id)}
                      onToggle={handleToggle}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onAddChild={handleAddChild}
                      onAIAssist={onOpenAIAssist}
                      onSelect={onRequirementSelect}
                      isReadOnly={isReadOnly}
                      expandedIds={expandedIds}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        )}

        {/* Editor Modal */}
        <RequirementEditorModal
          isOpen={showEditor}
          onClose={() => setShowEditor(false)}
          requirement={editingRequirement}
          projectId={projectId}
          tenantId={tenantId}
          userId={userId}
          categories={categories}
          hierarchyTypes={hierarchyTypes}
          parentOptions={getParentOptions}
          onSave={handleSaveRequirement}
          isSaving={isSaving}
          defaultHierarchyType={defaultHierarchyType}
        />
      </div>
    </WidgetErrorBoundary>
  );
};

export default RequirementsHierarchy;
